import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import * as Lucide from "lucide-react";

// Map German category names -> lucide-react icon names.
const ICON_NAME = {
  Aussteller: "Store",
  Baustromverteiler: "Zap",
  Bedrohungsalarm: "TriangleAlert",
  "Beschilderung (Fluchtweg)": "Signpost",
  "Beschilderung (StVO)": "Signpost",
  "Bühne": "Music",
  Catering: "Utensils",
  "Ein/Ausfahrt Veranstaltungsgelände": "ArrowRightLeft",
  "Einlasskontrolle/Schleuse": "Ticket",
  Einsatzleitungszentrale: "RadioTower",
  Fahrgeschäft: "FerrisWheel",
  Feueralarm: "Flame",
  Feuerwehr: "Flame",
  Fotobox: "Camera",
  Geldautomat: "Banknote",
  GoldenOldie: "Car",
  Hundekotbeutelspender: "Dog",
  Infopoint: "Info",
  "Kameraüberwachung": "Cctv",
  "Ladestation (EV)": "BatteryCharging",
  Meldepunkt: "BellRing",
  Notausgang: "DoorOpen",
  Parkplatz: "SquareParking",
  Polizei: "Shield",
  Rettungsdienst: "Ambulance",
  Rettungspunkt: "Cross",
  Sammelplatz: "Users",
  Sanitätsdienstalarm: "Siren",
  "Shade Zone": "Umbrella",
  "Shuttle (Haltestelle)": "Bus",
  Sicherheitslautsprecher: "Megaphone",
  Terrorsperre: "ShieldAlert",
  "Terrorsperre betreut": "ShieldAlert",
  "Terrorsperre unbetreut": "ShieldAlert",
  Trinkwasserspender: "Droplet",
  Veranstaltungsleitung: "ClipboardList",
  Wasserversorgung: "Droplets",
  WC: "Toilet",
  "WC (barrierefrei)": "Accessibility",
  Weinausschank: "Wine",
  Sektbar: "Martini",
  "Spezialitäten": "ChefHat",
  Backwaren: "Croissant",
  Crepes: "CakeSlice",
  Flammkuchen: "Pizza",
  Grillprodukte: "Beef",
  "Grillspezialitäten - Spieße": "Flame",
  Promotion: "Megaphone",
  "Süßspeisen": "IceCreamCone",
  "Winzerteller, Backwaren, Wurst etc.": "UtensilsCrossed",
  "Wurst, Pommes, Getränke": "Sandwich",
  "Wurst, Pommes, tapas, Getränke": "Ham",
};

const COLORS = {
  WC: "#0EA5E9",
  "WC (barrierefrei)": "#0284C7",
  Feuerwehr: "#EF4444",
  Feueralarm: "#DC2626",
  Bedrohungsalarm: "#DC2626",
  Parkplatz: "#D97706",
  Rettungspunkt: "#10B981",
  Rettungsdienst: "#059669",
  Sanitätsdienstalarm: "#047857",
  Sammelplatz: "#F97316",
  "Bühne": "#8B5CF6",
  Catering: "#EC4899",
  Notausgang: "#22C55E",
  Polizei: "#1D4ED8",
  "Kameraüberwachung": "#6366F1",
  Infopoint: "#0EA5E9",
  Fahrgeschäft: "#D946EF",
  GoldenOldie: "#A16207",
  Meldepunkt: "#EA580C",
  Aussteller: "#7C3AED",
  Baustromverteiler: "#EAB308",
  "Ladestation (EV)": "#16A34A",
  "Shuttle (Haltestelle)": "#0891B2",
  Trinkwasserspender: "#38BDF8",
  Wasserversorgung: "#0EA5E9",
  Terrorsperre: "#B91C1C",
  "Terrorsperre betreut": "#B91C1C",
  "Terrorsperre unbetreut": "#7F1D1D",
  "Shade Zone": "#F59E0B",
  Fotobox: "#DB2777",
  Geldautomat: "#0D9488",
  Veranstaltungsleitung: "#4F46E5",
  Einsatzleitungszentrale: "#4338CA",
  Sicherheitslautsprecher: "#9333EA",
  "Einlasskontrolle/Schleuse": "#0891B2",
  Hundekotbeutelspender: "#65A30D",
  "Beschilderung (Fluchtweg)": "#059669",
  "Beschilderung (StVO)": "#2563EB",
  "Ein/Ausfahrt Veranstaltungsgelände": "#CA8A04",
  Weinausschank: "#7C1D3F",
  Sektbar: "#CA8A04",
  "Spezialitäten": "#0F766E",
  Backwaren: "#B45309",
  Crepes: "#D97706",
  Flammkuchen: "#EA580C",
  Grillprodukte: "#9A3412",
  "Grillspezialitäten - Spieße": "#C2410C",
  Promotion: "#7C3AED",
  "Süßspeisen": "#DB2777",
  "Winzerteller, Backwaren, Wurst etc.": "#65A30D",
  "Wurst, Pommes, Getränke": "#EA580C",
  "Wurst, Pommes, tapas, Getränke": "#B45309",
};

const PALETTE = [
  "#0EA5E9", "#EF4444", "#D97706", "#10B981", "#F97316", "#8B5CF6",
  "#EC4899", "#22C55E", "#6366F1", "#14B8A6", "#DB2777", "#3B82F6", "#84CC16",
];

function hash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

export function catColor(name) {
  if (!name) return "#64748B";
  return COLORS[name] || PALETTE[hash(name) % PALETTE.length];
}

export function getIconComponent(name) {
  const n = ICON_NAME[name] || "MapPin";
  return Lucide[n] || Lucide.MapPin;
}

export function CategoryIcon({ category, size = 18, className, color }) {
  const Icon = getIconComponent(category);
  return <Icon size={size} className={className} color={color} />;
}

export function pinHtml(category, active = false, opts = {}) {
  const color = opts.color || catColor(category);
  const Icon = getIconComponent(category);
  const svg = renderToStaticMarkup(
    <Icon size={16} color="#ffffff" strokeWidth={2.5} />
  );
  const pulse = opts.pulse ? " go-pin--pulse" : "";
  return `<div class="go-pin${active ? " go-pin--active" : ""}${pulse}" style="--pin:${color}"><span class="go-pin__glyph">${svg}</span></div>`;
}
